import { createRnnoiseMicPipeline } from './rnnoisePipeline';

const ICE_SERVERS_CACHE = new Map();

export const createPeerConnection = (iceServers) => {
  return new RTCPeerConnection({ iceServers });
};

const NOISE_SUPPRESSION_STORAGE_KEY = 'lynx.voice.noiseSuppression';

export const readNoiseSuppressionPreference = () => {
  try {
    const raw = window.localStorage.getItem(NOISE_SUPPRESSION_STORAGE_KEY);
    if (raw === null) {
      return true;
    }
    return raw === '1' || raw === 'true';
  } catch (_) {
    return true;
  }
};

export const writeNoiseSuppressionPreference = (enabled) => {
  try {
    window.localStorage.setItem(NOISE_SUPPRESSION_STORAGE_KEY, enabled ? '1' : '0');
  } catch (_) {
    // ignore quota / private mode
  }
};

/**
 * Capture mic through RNNoise AudioWorklet chain when enabled.
 * Returns { stream, pipeline } — pipeline.destroy() must be called on hangup.
 */
export const acquireLocalAudio = async (noiseSuppression = true) => {
  const pipeline = await createRnnoiseMicPipeline(noiseSuppression);
  return {
    stream: pipeline.stream,
    pipeline,
  };
};

export const attachLocalAudio = (pc, stream) => {
  stream.getTracks().forEach((track) => pc.addTrack(track, stream));
};

export const canShareScreen = () => (
  typeof navigator !== 'undefined'
  && typeof navigator.mediaDevices?.getDisplayMedia === 'function'
);

const DISPLAY_MEDIA_OPTION_SETS = [
  {
    audio: true,
    systemAudio: 'include',
    selfBrowserSurface: 'include',
    surfaceSwitching: 'include',
    monitorTypeSurfaces: 'include',
  },
  { audio: true, systemAudio: 'include' },
  { audio: true },
  { audio: false },
];

const isUserDisplayMediaAbort = (error) => (
  error?.name === 'NotAllowedError' || error?.name === 'AbortError'
);

export const prepareScreenAudioTrack = (track) => {
  if (!track || track.kind !== 'audio') {
    return;
  }
  try {
    track.contentHint = 'music';
  } catch (_) {
    // ignore unsupported hint
  }
  try {
    track.enabled = true;
  } catch (_) {
    // ignore
  }
  if (typeof track.applyConstraints !== 'function') {
    return;
  }
  track.applyConstraints({
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
  }).catch(() => {});
  track.applyConstraints({ voiceIsolation: false }).catch(() => {});
};

export const acquireScreenShare = async () => {
  const video = {
    frameRate: { ideal: 12, max: 20 },
    width: { max: 1920 },
    height: { max: 1080 },
  };
  let stream;
  let lastError;
  for (const extra of DISPLAY_MEDIA_OPTION_SETS) {
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video, ...extra });
      lastError = null;
      break;
    } catch (error) {
      lastError = error;
      if (isUserDisplayMediaAbort(error)) {
        throw error;
      }
    }
  }
  if (!stream) {
    throw lastError || new Error('screen share failed');
  }
  stream.getAudioTracks().forEach(prepareScreenAudioTrack);
  stream.getVideoTracks().forEach((track) => {
    try {
      track.contentHint = 'detail';
    } catch (_) {
      // ignore unsupported hint
    }
  });
  return stream;
};

/**
 * Mix mic + tab audio into one track and replace the existing mic sender.
 * A second audio m-line is unreliable in Chrome; replaceTrack needs no new SDP.
 */
export const createCallAudioMixer = (micStream, screenStream) => {
  const micTrack = micStream?.getAudioTracks?.()?.[0];
  const screenTracks = screenStream?.getAudioTracks?.() || [];
  if (!micTrack || !screenTracks.length || typeof AudioContext === 'undefined') {
    return null;
  }

  // Disable AEC on mic track so Chrome doesn't duck screen audio when user speaks
  micTrack.applyConstraints({
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
  }).catch(() => {});

  let audioContext;
  try {
    audioContext = new AudioContext({ sampleRate: 48000 });
  } catch (_) {
    try {
      audioContext = new AudioContext();
    } catch (error) {
      return null;
    }
  }
  let micSource = null;
  let screenSource = null;
  let screenGain = null;
  let destination = null;
  try {
    destination = audioContext.createMediaStreamDestination();
    try {
      destination.channelCount = 2;
      destination.channelCountMode = 'explicit';
    } catch (_) {
      // ignore
    }
    micSource = audioContext.createMediaStreamSource(new MediaStream([micTrack]));
    micSource.connect(destination);
    screenSource = audioContext.createMediaStreamSource(new MediaStream(screenTracks));
    screenGain = audioContext.createGain();
    screenGain.gain.value = 1;
    screenSource.connect(screenGain);
    screenGain.connect(destination);
  } catch (error) {
    console.warn('[call] audio mixer failed', error);
    if (audioContext.state !== 'closed') {
      audioContext.close().catch(() => {});
    }
    return null;
  }

  const resume = () => {
    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {});
    }
  };
  resume();
  audioContext.addEventListener('statechange', resume);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', resume);
  }

  const track = destination.stream.getAudioTracks()[0] || null;
  if (!track) {
    document.removeEventListener?.('visibilitychange', resume);
    audioContext.close().catch(() => {});
    return null;
  }
  prepareScreenAudioTrack(track);

  return {
    stream: destination.stream,
    track,
    screenGain,
    setScreenVolume(vol) {
      if (screenGain) {
        screenGain.gain.value = Math.max(0, Math.min(2, vol));
      }
    },
    destroy() {
      // Restore AEC on mic track for normal voice call
      micTrack.applyConstraints({
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      }).catch(() => {});

      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', resume);
      }
      try {
        audioContext.removeEventListener('statechange', resume);
      } catch (_) {
        // ignore
      }
      try {
        micSource?.disconnect();
      } catch (_) {
        // ignore
      }
      try {
        screenSource?.disconnect();
      } catch (_) {
        // ignore
      }
      try {
        screenGain?.disconnect();
      } catch (_) {
        // ignore
      }
      destination?.stream?.getTracks?.().forEach((item) => {
        try {
          item.stop();
        } catch (_) {
          // ignore
        }
      });
      if (audioContext.state !== 'closed') {
        audioContext.close().catch(() => {});
      }
    },
  };
};

export const replaceOutgoingMicTrack = async (pc, nextTrack) => {
  if (!pc || !nextTrack) {
    return false;
  }
  const audioSenders = pc.getSenders().filter((sender) => (
    sender.track?.kind === 'audio' || Boolean(sender.dtmf)
  ));
  const sender = audioSenders.find((item) => item.track) || audioSenders[0];
  if (sender) {
    await sender.replaceTrack(nextTrack);
    return false;
  }
  pc.addTrack(nextTrack);
  return true;
};

/**
 * Chrome <audio> plays only one MediaStream audio track and ignores tracks added
 * after srcObject is set. Play each remote audio track on its own element.
 */
export const createRemoteAudioPlayer = () => {
  const elements = new Map();
  let host = null;

  const ensureHost = () => {
    if (host || typeof document === 'undefined') {
      return host;
    }
    host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;clip:rect(0,0,0,0);';
    document.body.appendChild(host);
    return host;
  };

  const stopTrack = (track) => {
    const entry = elements.get(track?.id);
    if (!entry) {
      return;
    }
    try {
      track.removeEventListener('ended', entry.onEnded);
      if (entry.tryPlay) {
        track.removeEventListener('unmute', entry.tryPlay);
      }
    } catch (_) {
      // ignore
    }
    entry.el.pause();
    entry.el.srcObject = null;
    entry.el.remove();
    elements.delete(track.id);
  };

  const addTrack = (track) => {
    if (!track || track.kind !== 'audio' || elements.has(track.id)) {
      return;
    }
    const el = document.createElement('audio');
    el.autoplay = true;
    el.playsInline = true;
    el.setAttribute('playsinline', 'true');
    el.muted = false;
    el.volume = masterVolume;
    el.srcObject = new MediaStream([track]);
    const onEnded = () => stopTrack(track);
    const tryPlay = () => {
      el.play().catch(() => {});
    };
    track.addEventListener('ended', onEnded);
    track.addEventListener('unmute', tryPlay);
    ensureHost()?.appendChild(el);
    tryPlay();
    elements.set(track.id, { el, onEnded, tryPlay });
  };

  let masterVolume = 1;

  const setVolume = (vol) => {
    masterVolume = Math.max(0, Math.min(1, vol));
    elements.forEach((entry) => {
      entry.el.volume = masterVolume;
    });
  };

  return {
    addTrack,
    removeTrack: stopTrack,
    setVolume,
    getVolume: () => masterVolume,
    destroy() {
      Array.from(elements.values()).forEach((entry) => {
        entry.el.pause();
        entry.el.srcObject = null;
        entry.el.remove();
      });
      elements.clear();
      host?.remove();
      host = null;
    },
  };
};

const audioTrackIds = (stream) => (stream?.getAudioTracks?.() || []).map((track) => track.id);

const isAudioSender = (sender) => (
  sender?.track?.kind === 'audio' || Boolean(sender?.dtmf)
);

export const attachOrReplaceScreenAudioTrack = async (pc, track, stream, micStream) => {
  if (!pc || !track) {
    return false;
  }
  const micIds = new Set(audioTrackIds(micStream));
  const screenSender = pc.getSenders().find((sender) => (
    isAudioSender(sender) && (!sender.track || !micIds.has(sender.track.id))
  ));
  if (screenSender) {
    await screenSender.replaceTrack(track);
    return false;
  }
  pc.addTrack(track, stream);
  return true;
};

export const clearScreenSenders = async (pc, micStream) => {
  if (!pc) {
    return;
  }
  const micIds = new Set(audioTrackIds(micStream));
  for (const sender of pc.getSenders()) {
    if (sender.track?.kind === 'video') {
      await sender.replaceTrack(null);
      continue;
    }
    if (isAudioSender(sender) && sender.track && !micIds.has(sender.track.id)) {
      await sender.replaceTrack(null);
    }
  }
};

export const attachOrReplaceVideoTrack = async (pc, track, stream) => {
  if (!pc || !track) {
    return false;
  }
  const sender = pc.getSenders().find((item) => item.track?.kind === 'video');
  if (sender) {
    await sender.replaceTrack(track);
    return false;
  }
  pc.addTrack(track, stream);
  return true;
};

export const clearVideoSender = async (pc) => {
  if (!pc) {
    return;
  }
  const sender = pc.getSenders().find((item) => item.track?.kind === 'video');
  if (sender?.track) {
    await sender.replaceTrack(null);
  }
};

export const replaceLocalAudioTrack = async (pc, prevStream, nextStream) => {
  const nextTrack = nextStream?.getAudioTracks?.()?.[0];
  if (!pc || !nextTrack) {
    return;
  }
  const sender = pc.getSenders().find((item) => item.track && item.track.kind === 'audio');
  if (sender) {
    await sender.replaceTrack(nextTrack);
  } else {
    pc.addTrack(nextTrack, nextStream);
  }
  // Do not stop prevStream here when it is a destination stream owned by a pipeline;
  // caller is responsible for pipeline lifecycle.
};

export const stopMediaStream = (stream) => {
  if (!stream) return;
  stream.getTracks().forEach((track) => track.stop());
};

export const closePeerConnection = (pc) => {
  if (!pc) return;
  pc.onicecandidate = null;
  pc.ontrack = null;
  pc.onconnectionstatechange = null;
  pc.close();
};

export const normalizeIceServers = (payload) => {
  const servers = payload?.ice_servers || payload?.iceServers || [];
  return servers.map((server) => ({
    urls: server.urls,
    username: server.username || undefined,
    credential: server.credential || undefined,
  }));
};

export const cacheIceServers = (callId, servers) => {
  if (callId) {
    ICE_SERVERS_CACHE.set(String(callId), servers);
  }
  return servers;
};

export const getCachedIceServers = (callId) => ICE_SERVERS_CACHE.get(String(callId)) || [];

export const clearCachedIceServers = (callId) => {
  if (callId) {
    ICE_SERVERS_CACHE.delete(String(callId));
  }
};

export const createMicLevelMonitor = (stream, onLevel) => {
  const audioContext = new AudioContext();
  const source = audioContext.createMediaStreamSource(stream);
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 512;
  analyser.smoothingTimeConstant = 0.82;
  source.connect(analyser);

  const data = new Uint8Array(analyser.fftSize);
  let rafId = null;
  let stopped = false;

  const tick = () => {
    if (stopped) {
      return;
    }
    analyser.getByteTimeDomainData(data);
    let sumSquares = 0;
    for (let i = 0; i < data.length; i += 1) {
      const sample = (data[i] - 128) / 128;
      sumSquares += sample * sample;
    }
    const rms = Math.sqrt(sumSquares / data.length);
    const level = Math.min(1, rms * 9);
    onLevel(level);
    rafId = window.requestAnimationFrame(tick);
  };

  return {
    start() {
      if (audioContext.state === 'suspended') {
        audioContext.resume().catch(() => {});
      }
      rafId = window.requestAnimationFrame(tick);
    },
    stop() {
      stopped = true;
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      source.disconnect();
      analyser.disconnect();
      audioContext.close().catch(() => {});
    },
  };
};
